import { createSlice, current } from "@reduxjs/toolkit";
import { paradiddle as defaultScore, EMPTY_SCORE } from "../components/compose/sample-score";
import { getScoreVoices as getScoreVoicesUtil } from "../utils/score";
//import defaultScore from '../../data/default-score';
//import { smallMultiPart as defaultScore } from "../components/compose/sample-score";
import {
  modifyNote as modifyNoteService,
  toggleOrnament,
  setRepeat,
  incDecSelectedNote,
  lastPitch,
} from "../services/score-service";
import { mutatePart } from "../services/mutate/mutate-service";
import { addMeasure as addMeasureService, 
         appendGeneratedMeasures as appendGeneratedMeasuresService,
         deleteMeasure as deleteMeasureService,
        updateTimeSig as updateTimeSigService } from '../services/measure-service';
import { getEmptyMeasure } from "../helpers/score";
import { HAND_HOME, letterInKey, letterAbove, transpose, sortPitches } from "../lib/piano";
import { baseOf, ENSEMBLES, MAX_PARTS, insertionIndex, instrumentOf, isPitched, missingFrom, nextPartKey, partKeys, soundsOf, voicesFor, staffView } from "../lib/instruments";
import { noteQuarterDuration } from "../lib/book-practice-score";
import { playbackOrder, soundingMeasure, keyAt, DYNAMIC_LEVELS, TEMPO_CHANGE_RATIO } from "../lib/score-structure";
import { insertCopiedMeasure } from "../services/measure-editing";
import { adjustMeasure } from "../services/rhythm-variations";
import { scorePrint } from "../lib/stats/refs";
import { withTakes, showingTake, nextTake, withoutTake, withRule, takeToPlay } from "../lib/takes";
import { ROOT, MAX_BRANCHES, MAX_DEPTH, findNode, pathTo, subtreeIds, uniqueName } from "../lib/rhythm-tree";
import _ from "lodash";
import { createSelector } from "reselect";
import { start as startToneJs, stop as stopToneJs } from "../lib/tone";
import {
  NON_ACCENT_VELOCITY,
  GRACE_VELOCITY,
  ACCENT_VELOCITY,
  DEFAULT_TEMPO,
  NOTE_CONFIG,
} from "../consts/score";
import {
  DEFAULT_MUTATION,
  DEFAULT_TIME_SIG,
} from "../consts/score";
import {
  ACCENT,
  FLAM,
  DIDDLE,
  CHEESE,
  BUZZ,
  LEFT_STICKING,
  RIGHT_STICKING,
  STACCATO,
  MARCATO,
  DOWN_BOW,
  UP_BOW,
  OPEN,
  STOPPED,
} from "../consts/ornaments";

export {
  ACCENT,
  FLAM,
  DIDDLE,
  CHEESE,
  BUZZ,
  LEFT_STICKING,
  RIGHT_STICKING,
};

const initialState = {
  // What the score was opened from, for its stats: { refs, print } (src/lib/stats/refs.js).
  origin: null,
  tempo: DEFAULT_TEMPO,
  timeSig: DEFAULT_TIME_SIG,
  score: defaultScore,
  /**
   * [{
   *  type: 'swap',
   *  context: 'E5',
   *  config: {
   *    probability: 0.25
   * }
   * }]
   *
   * */
  mutations: [DEFAULT_MUTATION],
  dynamic: false,
  scrollAmount: {
    top: 0,
    left: 0,
  },
  name: "",
  // The variation tree, once it has branches: { nodes, rootScore, currentId }.
  tree: null,
  voices: {
    drumset: {
      kickSelected: false,
      snareSelected: true,
      hiHatSelected: false,
      rideSelected: false,
      hiHatFootSelected: false,
      tom1Selected: false,
      tom2Selected: false,
      tom3Selected: false,
      tom4Selected: false,
    },
    snare: {
      snareSelected: true,
      pingSelected: false,
      stickClickSelected: false,
      crossoverSelected: false,
      backstickSelected: false,
      buttSelected: false,
      rimSelected: false,
    },
    tenors: {
      spockSelected: false,
      t1Selected: true,
      t2Selected: false,
      t3Selected: false,
      t4Selected: false,
    },
    bass: {
      b1Selected: true,
      b2Selected: false,
      b3Selected: false,
      b4Selected: false,
      b5Selected: false,
    },
    cymbals: {
      crashSelected: true,
      chokeSelected: false,
    },
  },
  // The sounds chosen for each percussion instrument that has several (its staff positions).
  sounds: {},
  // Pitch entry, on a piano or any pitched part: the length typed notes get (TrueChops units, 8 =
  // quarter), the last pitch on each staff (typed letters go to the nearest octave: `last` for the
  // piano's hands, `parts` by part for the rest), and the note just written, which Shift+letter, the
  // arrows and Backspace act on until you move the selection.
  pianoEntry: { duration: 8, last: [...HAND_HOME], parts: {}, lastEntered: null },
  selectedPartIndex: 0,

  /*
  selectedNoteIndex: {
    measureIndex: 0,
    partIndex: 1,
    voiceIndex: 0,
    noteIndex: 1,
  },*/

  repeat: {
    //start: 0,
    //end: 1,
  },

  dotSelected: false,
  tuplet: {
    selected: false,
    actual: 3,
    normal: 2,
    type: 8, //VF duration
  },
  isPlaying: false,
  // Each playing of the score is a pass, so a bar's takes (src/lib/takes.js) can change from one to the next.
  takePass: 0,
};

// How often a roll or tremolo strikes.
const ROLL_SECONDS = 0.07;
const plain = (value) => JSON.parse(JSON.stringify(value));
// Score always shows the current branch of the variation tree; the tree keeps the others.
function keepCurrentBranch(state) {
  const tree = state.tree;
  if (tree.currentId === ROOT) tree.rootScore = plain(state.score);
  else { const node = findNode(tree.nodes, tree.currentId); if (node) node.score = plain(state.score); }
}
function showBranch(state, id) {
  keepCurrentBranch(state);
  state.tree.currentId = id;
  state.score = plain(id === ROOT ? state.tree.rootScore : findNode(state.tree.nodes, id).score);
  state.repeat = {};
  state.selectedPartIndex = 0;
  state.selectedNoteIndex = null;
}

// The selected note and its voice, as editable drafts, or null.
function selectedVoiceDraft(state) {
  const index = state.selectedNoteIndex;
  const voice = index && state.score.measures[index.measureIndex]?.parts[index.partIndex]?.voices[index.voiceIndex];
  return voice?.notes[index.noteIndex] ? voice : null;
}
function selectedNoteDraft(state) {
  return selectedVoiceDraft(state)?.notes[state.selectedNoteIndex.noteIndex] || null;
}

// Older saved states predate piano entry.
function pianoEntry(state) {
  if (!state.pianoEntry) state.pianoEntry = { duration: 8, last: [...HAND_HOME], lastEntered: null };
  return state.pianoEntry;
}
function rememberPitch(entry, instrument, hand, pitch) {
  if (baseOf(instrument) === "piano") { entry.last[hand] = pitch; return; }
  entry.parts ||= {};
  (entry.parts[instrument] ||= [])[hand] = pitch;
}
// The selected pitched part and staff (a piano's hand), or null when the selection isn't on one.
function pianoSelection(state) {
  const index = state.selectedNoteIndex;
  const part = index && state.score.measures[index.measureIndex]?.parts[index.partIndex];
  return isPitched(part?.instrument) && part.voices[index.voiceIndex] ? { part, hand: index.voiceIndex } : null;
}
// The note piano keys act on: the one just written while it's still there, else the selection.
function pianoTarget(state, selection) {
  const last = state.pianoEntry?.lastEntered, selected = state.selectedNoteIndex;
  const index = last && last.partIndex === selected.partIndex && last.voiceIndex === selected.voiceIndex ? last : selected;
  const note = state.score.measures[index.measureIndex]?.parts[index.partIndex]?.voices[index.voiceIndex]?.notes[index.noteIndex];
  return note ? { note, index } : null;
}

// A score's bars, and the bars of their takes, which keep the same parts.
function everyBar(score, change) {
  for (const measure of score.measures) {
    change(measure);
    for (const take of measure.takes || []) if (take.score?.measures?.[0]) change(take.score.measures[0]);
  }
}
// One more part of each instrument, each where a score lists it. Returns their keys.
function addParts(state, ids) {
  const added = [], selected = partKeys(state.score)[state.selectedNoteIndex?.partIndex];
  for (const id of ids) {
    const keys = partKeys(state.score);
    if (keys.length >= MAX_PARTS || !instrumentOf(id)) break;
    const key = nextPartKey(keys, baseOf(id)), at = insertionIndex(keys, key);
    state.score.parts[key] = { enabled: true };
    everyBar(state.score, (bar) => bar.parts.splice(Math.min(at, bar.parts.length), 0, getEmptyMeasure(bar.timeSig, [key]).parts[0]));
    added.push(key);
  }
  // What was selected stays selected, wherever its part is now.
  if (selected != null) state.selectedNoteIndex.partIndex = partKeys(state.score).indexOf(selected);
  if (state.pianoEntry) state.pianoEntry.lastEntered = null;
  return added;
}
// The note after one in a voice: the next in its measure, else the first of the next measure's.
function noteAfter(state, { measureIndex, partIndex, voiceIndex, noteIndex }) {
  const notes = (index) => state.score.measures[index]?.parts[partIndex]?.voices[voiceIndex]?.notes;
  return notes(measureIndex)?.[noteIndex + 1] || notes(measureIndex + 1)?.[0] || null;
}

const scoreSlice = createSlice({
  name: "score",
  initialState,
  reducers: {
    updateScore(state, action) {
      const { score, name, tempo, mutations, tree, ref, refs } = action.payload;
      state.score = score;
      const named = [...(refs || []), ...(ref ? [ref] : [])];
      state.origin = named.length ? { refs: named.slice(0, 12), print: scorePrint(score) } : null;
      state.tree = tree?.nodes?.length ? { nodes: plain(tree.nodes), rootScore: plain(score), currentId: ROOT } : null;
      state.name = name;
      state.tempo = tempo;
      state.repeat = {};
      state.selectedPartIndex = 0;
      state.selectedNoteIndex = null;
      state.dynamic = mutations && mutations.length > 0;

      state.mutations = [];
      if (mutations && mutations.length > 0) {
        mutations.forEach((mutation) => {
          const { type, context, grid, config } = mutation;

          //JARED_TODO: change this
          let _config = config;
          if(typeof config === 'string') {
            _config = JSON.parse(config);
          }

          state.mutations.push({ type, context, grid, config: _config });
        });
      } else {
        state.mutations.push(DEFAULT_MUTATION);
      }
    },
    updateTempo(state, action) {
      state.tempo = action.payload;
    },
    updateTimeSig(state, action) {
      updateTimeSigService(state, action);
    },
    setScrollAmount(state, action) {
      state.scrollAmount = action.payload;
    },
    toggleIsPlaying(state) {
      state.isPlaying = !state.isPlaying;
    },
    startStop(state) {
      if (!state.isPlaying) {
        startToneJs();
      } else {
        stopToneJs();
        state.takePass = (state.takePass || 0) + 1;
      }

      state.isPlaying = !state.isPlaying;
    },
    setRepeatStart(state) {
      setRepeat(state, "start");
    },
    setRepeatEnd(state) {
      setRepeat(state, "end");
    },
    selectNote(state, action) {
      state.measureSelection = false;
      if (state.pianoEntry) state.pianoEntry.lastEntered = null;
      const { measureIndex, partIndex, voiceIndex, noteIndex } = action.payload;

      //If the user is selecting the same note in the score, unselect the note
      if (_.isEqual(action.payload, state.selectedNoteIndex)) {
        _.unset(state, "selectedNoteIndex");
      } else {
        state.selectedNoteIndex = {
          measureIndex,
          partIndex,
          voiceIndex,
          noteIndex,
        };
      }

      if (partIndex !== state.selectedPartIndex) {
        state.selectedPartIndex = partIndex;
      }

      //update the selected timeSig
      state.timeSig = state.score.measures[measureIndex].timeSig;
    },
    selectMeasure(state, action) {
      const measure = state.score.measures[action.payload];
      if (!measure) { state.selectedNoteIndex = null; return; }
      const partIndex = Math.max(0, measure.parts.findIndex((part) => state.score.parts[part.instrument]?.enabled));
      state.selectedNoteIndex = { measureIndex: action.payload, partIndex, voiceIndex: 0, noteIndex: 0 };
      state.selectedPartIndex = partIndex;
      state.measureSelection = true;
      state.timeSig = measure.timeSig;
    },
    // One undoable edit that varies the given measures, e.g. every measure in the score.
    adjustMeasures(state, action) {
      const { category, instrument, indexes } = action.payload;
      if (state.score.parts[instrument]?.enabled === false) return;
      for (const index of indexes) {
        if (state.score.measures[index]) state.score.measures[index] = adjustMeasure(state.score.measures[index], category, Math.random, instrument);
      }
    },
    replaceMeasure(state, action) {
      const { index, measure, expected } = action.payload;
      if (!measure || !_.isEqual(state.score.measures[index], expected)) return;
      state.score.measures[index] = _.cloneDeep(measure);
    },
    // Several bars at once, as one change (an idea for the whole score): [{ index, measure, expected }],
    // none of them changed unless every one is still as expected.
    replaceMeasures(state, action) {
      const changes = action.payload?.changes || [];
      if (!changes.length || changes.some(({ index, measure, expected }) => !measure || !_.isEqual(state.score.measures[index], expected))) return;
      for (const { index, measure } of changes) state.score.measures[index] = _.cloneDeep(measure);
    },
    pasteMeasure(state, action) {
      insertCopiedMeasure(state, action.payload.measure, state.selectedNoteIndex?.measureIndex, action.payload.side);
    },
    // A bar's takes (src/lib/takes.js): other versions added to it ({ index, takes: [{ measure or
    // score, ref, how, similarity }] }), one shown ({ index, id }, or the next), its rule changed
    // ({ index, play, minSimilarity, pinned }), one removed ({ index, id }).
    addTakes(state, action) {
      const { index, takes } = action.payload, measure = state.score.measures[index];
      if (measure) state.score.measures[index] = withTakes(_.cloneDeep(measure), takes);
    },
    showTake(state, action) {
      const { index, id } = action.payload, measure = state.score.measures[index];
      if (measure) state.score.measures[index] = showingTake(_.cloneDeep(measure), id ?? nextTake(measure));
      if (state.selectedNoteIndex?.measureIndex === index && !state.measureSelection) state.selectedNoteIndex = { ...state.selectedNoteIndex, noteIndex: 0 };
    },
    setTakeRule(state, action) {
      const { index, ...rule } = action.payload, measure = state.score.measures[index];
      if (measure) state.score.measures[index] = withRule(_.cloneDeep(measure), rule);
    },
    removeTake(state, action) {
      const { index, id } = action.payload, measure = state.score.measures[index];
      if (measure) state.score.measures[index] = withoutTake(_.cloneDeep(measure), id);
      if (state.selectedNoteIndex?.measureIndex === index && !state.measureSelection) state.selectedNoteIndex = { ...state.selectedNoteIndex, noteIndex: 0 };
    },
    selectPreviousNote(state) {
      state.measureSelection = false;
      if (state.pianoEntry) state.pianoEntry.lastEntered = null;
      incDecSelectedNote(state, false);
    },
    selectNextNote(state) {
      state.measureSelection = false;
      if (state.pianoEntry) state.pianoEntry.lastEntered = null;
      incDecSelectedNote(state, true);
    },
    toggleAccent(state) {
      toggleOrnament(state, ACCENT);
    },
    toggleBuzz(state) {
      toggleOrnament(state, BUZZ);
    },
    toggleLeftSticking(state) {
      toggleOrnament(state, LEFT_STICKING, RIGHT_STICKING);
    },
    toggleRightSticking(state) {
      toggleOrnament(state, RIGHT_STICKING, LEFT_STICKING);
    },
    toggleFlam(state) {
      toggleOrnament(state, FLAM, DIDDLE + CHEESE);
    },
    toggleDiddle(state) {
      toggleOrnament(state, DIDDLE, FLAM + CHEESE);
    },
    toggleCheese(state) {
      toggleOrnament(state, CHEESE, FLAM + DIDDLE);
    },
    addMeasure(state, action) {
      addMeasureService(state, action);
    },
    deleteMeasure(state) {
      deleteMeasureService(state);
    },
    appendGeneratedMeasures(state, action) {
      appendGeneratedMeasuresService(state, action);
    },
    // Adds branches beneath the current one; `select` switches to the first of them.
    // A branch without a score starts as a copy of the current one.
    addTreeBranches(state, action) {
      const { branches, select = false } = action.payload;
      state.tree = state.tree || { nodes: [], rootScore: null, currentId: ROOT };
      const tree = state.tree, parentId = tree.currentId;
      if (pathTo(tree.nodes, parentId).length >= MAX_DEPTH) return;
      keepCurrentBranch(state);
      const added = branches.slice(0, Math.max(0, MAX_BRANCHES - tree.nodes.length)).map(({ id, name, score }) => {
        const node = { id, parentId, name: uniqueName(tree.nodes, parentId, name), score: plain(score || state.score) };
        tree.nodes.push(node);
        return node;
      });
      if (select && added.length) showBranch(state, added[0].id);
    },
    selectTreeBranch(state, action) {
      const id = action.payload, tree = state.tree;
      if (!tree || id === tree.currentId || (id !== ROOT && !findNode(tree.nodes, id))) return;
      showBranch(state, id);
    },
    renameTreeBranch(state, action) {
      const node = state.tree && findNode(state.tree.nodes, action.payload.id);
      const name = String(action.payload.name || "").trim().slice(0, 60);
      if (node && name) node.name = name;
    },
    // Removes a branch and everything beneath it, moving to its parent if needed.
    deleteTreeBranch(state, action) {
      const tree = state.tree, node = tree && findNode(tree.nodes, action.payload);
      if (!node) return;
      const removed = subtreeIds(tree.nodes, node.id);
      if (removed.has(tree.currentId)) showBranch(state, node.parentId);
      tree.nodes = tree.nodes.filter((entry) => !removed.has(entry.id));
    },
    // Empties the score: one bar of rests, keeping its parts, their names and its key.
    clearScore(state) {
      const keys = partKeys(state.score);
      state.score = keys.length ? { ...state.score, measures: [getEmptyMeasure(state.score.measures[0].timeSig, keys)] } : EMPTY_SCORE
      state.selectedPartIndex = 0
      state.selectedNoteIndex = null
    },
    mutateNotes(state, action) {
      const { repeats, part } = typeof action.payload === "object" ? action.payload : { repeats: action.payload };
      const score = current(state.score);

      state.score.measures = mutatePart(score, current(state.mutations), repeats, part || partKeys(score)[0]);

      //We need to clear this out because mutating the notes might change the number of notes in the
      //score, which could cause the note index for the selected note to be for a non-existent note.
      state.selectedNoteIndex = null;
      //JARED_TODO: see if there is any way to get rid of this.
      state.dynamic = false;
    },
    updateMutateContext(state, action) {
      state.mutations[0].context = action.payload;
    },
    updateMutateGrid(state, action) {
      state.mutations[0].grid = action.payload;
    },
    updateMutateType(state, action) {
      state.mutations[0].type = action.payload;
    },
    //When user modifies a note in the score. Ex: 8th note to 16th note
    modifyNote(state, action) {
      const { type, isRest } = action.payload;
      let duration = NOTE_CONFIG[type].duration;
      const selectedNote = getSelectedNote(state);

      if (selectedNote) {
        if (isPitched(selectedNote.instrument)) pianoEntry(state).duration = duration;
        modifyNoteService(state, duration, isRest, selectedNote);
      }
    },
    // The key from a measure on: at the first measure it's the score's key; later, a key change
    // (none if it matches the key already in effect). A bare key sets the score's key.
    setKeySignature(state, action) {
      const { measureIndex = 0, key = "C" } = typeof action.payload === "string" ? { key: action.payload } : action.payload || {};
      const measure = state.score.measures[measureIndex];
      if (!measure) return;
      if (measureIndex === 0) {
        if (key !== "C") state.score.keySig = key; else delete state.score.keySig;
        delete measure.keySig;
      } else if (key === keyAt(state.score, measureIndex - 1)) delete measure.keySig;
      else measure.keySig = key;
    },
    // The selected note's dynamic (ppp to fff, sfz, rfz, fp, pf); choosing it again removes it.
    setDynamic(state, action) {
      const note = selectedNoteDraft(state);
      if (!note) return;
      if (!action.payload || note.dynamic === action.payload) delete note.dynamic;
      else note.dynamic = action.payload;
    },
    // Removes the selected note's dynamic and any crescendo or diminuendo over it.
    clearDynamics(state) {
      const voice = selectedVoiceDraft(state), at = state.selectedNoteIndex?.noteIndex;
      if (!voice) return;
      delete voice.notes[at].dynamic;
      const kept = (voice.hairpins || []).filter((hairpin) => !(at >= hairpin.start && at < hairpin.end));
      if (kept.length) voice.hairpins = kept; else delete voice.hairpins;
    },
    // A crescendo ("cresc") or diminuendo ("dim") from the selected note to the end of its
    // measure. Under an existing one, the same kind on its first note removes it and the other
    // kind switches it; on a later note, the same kind ends it there, and the other kind ends it
    // just before and starts the new one (a swell: < then >).
    toggleHairpin(state, action) {
      const voice = selectedVoiceDraft(state), at = state.selectedNoteIndex?.noteIndex, type = action.payload;
      if (!voice) return;
      const hairpins = voice.hairpins || [], index = hairpins.findIndex((hairpin) => at >= hairpin.start && at < hairpin.end);
      if (index < 0) { voice.hairpins = [...hairpins, { type, start: at, end: voice.notes.length }]; return; }
      const current = hairpins[index];
      if (current.start === at) {
        if (current.type === type) hairpins.splice(index, 1);
        else current.type = type;
      } else if (current.type === type) current.end = at + 1;
      else {
        const end = current.end;
        current.end = at;
        hairpins.splice(index + 1, 0, { type, start: at, end });
      }
      if (!hairpins.length) delete voice.hairpins;
    },
    // A form marking on the selected measure (barline, repeatStart, repeatEnd, repeatTimes,
    // ending, mark, jump, tempoChange, measureRepeat, slashes). Choosing the current value removes it.
    setMeasureMarking(state, action) {
      const { key, value } = action.payload;
      const measure = state.score.measures[state.selectedNoteIndex?.measureIndex];
      if (!measure) return;
      const current = measure[key];
      const same = Array.isArray(value) ? Array.isArray(current) && current.join() === value.join() : current === value;
      if (value == null || value === false || same) delete measure[key]; else measure[key] = value;
      if (key === "repeatTimes" && measure.repeatTimes) measure.repeatEnd = true;
      if (key === "repeatEnd" && !measure.repeatEnd) delete measure.repeatTimes;
      // A measure repeat and slashes both replace the written notes; only one at a time.
      if (key === "measureRepeat" && measure.measureRepeat) delete measure.slashes;
      if (key === "slashes" && measure.slashes) delete measure.measureRepeat;
    },
    // Writes a piano note at the selection and moves on: a typed letter (spelled in the key, in
    // the octave nearest the hand's last note) or an exact pitch from the on-screen keyboard.
    // With chord, the pitch joins the note just written (or the selected note) instead.
    pianoWrite(state, action) {
      const { letter, pitch: exact, chord = false } = action.payload;
      const selection = pianoSelection(state);
      if (!selection) return;
      const { part, hand } = selection, entry = pianoEntry(state), { instrument } = part;
      // Notes are typed as the part reads them, and kept as they sound.
      const view = staffView(instrument, keyAt(state.score, state.selectedNoteIndex.measureIndex), { concert: state.score.concert }), key = view.key;
      if (chord) {
        const target = pianoTarget(state, selection);
        if (target?.note.notes.length) {
          const written = exact || letterAbove(letter, target.note.notes.map(view.written), key);
          if (written) { const pitch = view.sounding(written); target.note.notes = sortPitches([...target.note.notes, pitch]); rememberPitch(entry, instrument, hand, pitch); }
          return;
        }
      }
      const pitch = view.sounding(exact || letterInKey(letter, view.written(lastPitch(state, instrument, hand)), key));
      const at = { ...state.selectedNoteIndex }, notes = part.voices[hand].notes, current = notes[at.noteIndex];
      modifyNoteService(state, entry.duration, false, { ...at, instrument, pitches: [pitch] });
      // When the entry length doesn't fit before the barline, the note keeps its own length.
      if (notes[at.noteIndex] === current) {
        current.notes = [pitch];
        incDecSelectedNote(state, true);
      }
      rememberPitch(entry, instrument, hand, pitch);
      entry.lastEntered = at;
    },
    // Moves the note just written (or the selected note) by semitones: 1, -1, 12 or -12.
    pianoTranspose(state, action) {
      const selection = pianoSelection(state), target = selection && pianoTarget(state, selection);
      if (!target?.note.notes.length) return;
      const key = keyAt(state.score, target.index.measureIndex);
      target.note.notes = sortPitches(target.note.notes.map((pitch) => transpose(pitch, action.payload, key)));
      rememberPitch(pianoEntry(state), selection.part.instrument, selection.hand, target.note.notes[target.note.notes.length - 1]);
    },
    // Backspace: the note just written (or the selected note) becomes a rest, and is selected.
    pianoRest(state) {
      const selection = pianoSelection(state), target = selection && pianoTarget(state, selection);
      if (!target) return;
      target.note.notes = [];
      delete target.note.ornaments;
      state.selectedNoteIndex = { ...target.index };
      pianoEntry(state).lastEntered = null;
    },
    // Moves the selection to the other hand, at the same point in the measure.
    pianoSwitchHand(state) {
      const selection = pianoSelection(state);
      if (!selection || selection.part.voices.length < 2) return;
      const { part, hand } = selection, { noteIndex } = state.selectedNoteIndex;
      const offset = (voice, end) => voice.notes.slice(0, end).reduce((sum, note, index) => sum + noteQuarterDuration(note, index, voice.tuplets || []), 0);
      const at = offset(part.voices[hand], noteIndex), other = part.voices[1 - hand];
      let index = other.notes.findIndex((note, i) => offset(other, i + 1) > at + 1e-9);
      if (index < 0) index = other.notes.length - 1;
      state.selectedNoteIndex = { ...state.selectedNoteIndex, voiceIndex: 1 - hand, noteIndex: index };
      pianoEntry(state).lastEntered = null;
    },

    togglePartEnabled(state, action) {
      const instrument = action.payload;
      state.score.parts[instrument].enabled =
        !state.score.parts[instrument].enabled;
      // A hidden part can't be written on.
      if (partKeys(state.score)[state.selectedPartIndex] === instrument) state.selectedNoteIndex = null;
    },
    deletePart(state, action) {
      const instrument = action.payload;
      _.unset(state.score.parts, instrument);

      everyBar(state.score, (bar) => { bar.parts = bar.parts.filter((part) => part.instrument !== instrument); });

      state.selectedNoteIndex = null;
      state.selectedPartIndex = 0;
    },
    // Adds a part for an instrument (its id), where a score lists it; another of an instrument the
    // score has gets its own number. Its panel opens, ready for a note to be clicked.
    addPart(state, action) {
      const [key] = addParts(state, [action.payload]);
      if (key) state.selectedPartIndex = partKeys(state.score).indexOf(key);
    },
    // Adds what a whole group (src/lib/instruments.js: ENSEMBLES) needs that the score doesn't have.
    addEnsemble(state, action) {
      const ensemble = ENSEMBLES.find((item) => item.id === action.payload);
      if (!ensemble) return;
      addParts(state, missingFrom(partKeys(state.score), ensemble));
      state.selectedPartIndex = state.selectedNoteIndex?.partIndex ?? 0;
    },
    // Moves a part up (by: -1) or down (by: 1) the score.
    movePart(state, action) {
      const { instrument, by } = action.payload, keys = partKeys(state.score), from = keys.indexOf(instrument), to = from + by;
      if (from < 0 || to < 0 || to >= keys.length) return;
      const selected = keys[state.selectedPartIndex], order = [...keys];
      order.splice(to, 0, order.splice(from, 1)[0]);
      everyBar(state.score, (bar) => { bar.parts = order.map((key) => bar.parts.find((part) => part.instrument === key)).filter(Boolean); });
      state.selectedPartIndex = Math.max(0, order.indexOf(selected));
      if (state.selectedNoteIndex) state.selectedNoteIndex.partIndex = state.selectedPartIndex;
    },
    // A part's own name, or back to its instrument's when empty.
    renamePart(state, action) {
      const { instrument, name } = action.payload, part = state.score.parts[instrument], text = String(name || "").trim().slice(0, 40);
      if (!part) return;
      if (text) part.name = text; else delete part.name;
    },
    // One part alone, to read and hear it as its player would; showAllParts brings the rest back.
    showOnlyPart(state, action) {
      for (const key of partKeys(state.score)) state.score.parts[key].enabled = key === action.payload;
      state.selectedNoteIndex = null;
      state.selectedPartIndex = Math.max(0, partKeys(state.score).indexOf(action.payload));
    },
    showAllParts(state) {
      for (const key of partKeys(state.score)) state.score.parts[key].enabled = true;
    },
    // Whether transposing parts are shown as they sound rather than as their players read them.
    toggleConcertPitch(state) {
      if (state.score.concert) delete state.score.concert; else state.score.concert = true;
    },
    // Which sounds a percussion instrument writes (the staff positions of soundsOf); at least one.
    toggleSound(state, action) {
      const { instrument, at } = action.payload, base = baseOf(instrument);
      state.sounds ||= {};
      const chosen = state.sounds[base] || [soundsOf(base)[0].at];
      const next = chosen.includes(at) ? chosen.filter((item) => item !== at) : [...chosen, at];
      if (next.length) state.sounds[base] = next;
    },
    // Ties the selected note to the next, which takes its pitches; again removes the tie.
    toggleTie(state) {
      const note = selectedNoteDraft(state);
      if (!note?.notes.length) return;
      if (note.tie) { delete note.tie; return; }
      const next = noteAfter(state, state.selectedNoteIndex);
      if (!next) return;
      next.notes = [...note.notes];
      note.tie = true;
      incDecSelectedNote(state, true);
    },
    // Starts a slur on the selected note (it shows to the next note), or ends the open one here, on
    // any later note, across barlines too. On a slur's first or last note, removes it.
    toggleSlur(state) {
      const note = selectedNoteDraft(state), { measureIndex, partIndex, voiceIndex, noteIndex } = state.selectedNoteIndex || {};
      if (!note?.notes.length) return;
      const notes = state.score.measures.flatMap((measure, bar) => (measure.parts[partIndex]?.voices[voiceIndex]?.notes || []).map((item, index) => ({ item, bar, index })));
      const at = notes.findIndex(({ bar, index }) => bar === measureIndex && index === noteIndex);
      if (note.slur === "start") {
        const end = notes.slice(at + 1).find(({ item }) => item.slur);
        if (end?.item.slur === "end") delete end.item.slur;
      }
      if (note.slur) { delete note.slur; return; }
      const before = notes.slice(0, at).reverse().find(({ item }) => item.slur);
      note.slur = before?.item.slur === "start" ? "end" : "start";
    },
    // A syllable of the lyrics on the selected note; empty removes it.
    setLyric(state, action) {
      const note = selectedNoteDraft(state), text = String(action.payload || "").trim().slice(0, 24);
      if (!note?.notes.length) return;
      if (text) note.lyric = text; else delete note.lyric;
    },
    // On to the next note that's played, for the next syllable.
    selectNextPlayed(state) {
      const { measureIndex, partIndex, voiceIndex, noteIndex } = state.selectedNoteIndex || {};
      if (measureIndex == null) return;
      for (let bar = measureIndex; bar < state.score.measures.length; bar++) {
        const notes = state.score.measures[bar].parts[partIndex]?.voices[voiceIndex]?.notes || [];
        const next = notes.findIndex((note, index) => (bar > measureIndex || index > noteIndex) && note.notes.length);
        if (next >= 0) { state.selectedNoteIndex = { measureIndex: bar, partIndex, voiceIndex, noteIndex: next }; return; }
      }
    },
    // Which voice of the selected bar is being written, on a one-staff instrument: the second is made
    // when it's first chosen, and goes again if it's left with nothing in it.
    chooseVoice(state, action) {
      const index = state.selectedNoteIndex, measure = index && state.score.measures[index.measureIndex], part = measure?.parts[index.partIndex], voice = action.payload;
      if (!isPitched(part?.instrument) || voicesFor(part.instrument) === 2) return;
      if (voice === 1 && !part.voices[1]) part.voices.push(getEmptyMeasure(measure.timeSig, [part.instrument]).parts[0].voices[0]);
      if (voice === 0 && part.voices[1]?.notes.every((note) => !note.notes.length)) part.voices.length = 1;
      if (part.voices[voice]) state.selectedNoteIndex = { ...index, voiceIndex: voice, noteIndex: 0 };
    },
    // Words over a note: pizz., con sord., solo. Empty, or the same again, removes them.
    setNoteText(state, action) {
      const note = selectedNoteDraft(state), text = String(action.payload || "").trim().slice(0, 32);
      if (!note) return;
      if (!text || note.text === text) delete note.text; else note.text = text;
    },
    // Staccato, tenuto, marcato and the like, on the selected note; bowings and horn marks exclude each other.
    toggleArticulation(state, action) {
      const letter = action.payload, others = { [DOWN_BOW]: UP_BOW, [UP_BOW]: DOWN_BOW, [OPEN]: STOPPED, [STOPPED]: OPEN, [MARCATO]: ACCENT }[letter];
      toggleOrnament(state, letter, others);
    },
    toggleKickSelected(state) {
      state.voices.drumset.kickSelected = !state.voices.drumset.kickSelected;
    },
    toggleSnareSelected(state) {
      state.voices.drumset.snareSelected = !state.voices.drumset.snareSelected;
    },
    toggleHiHatSelected(state) {
      state.voices.drumset.hiHatSelected = !state.voices.drumset.hiHatSelected;
    },
    toggleRideSelected(state) {
      state.voices.drumset.rideSelected = !state.voices.drumset.rideSelected;
    },
    toggleHiHatFootSelected(state) {
      state.voices.drumset.hiHatFootSelected =
        !state.voices.drumset.hiHatFootSelected;
    },
    toggleTom1Selected(state) {
      state.voices.drumset.tom1Selected = !state.voices.drumset.tom1Selected;
    },
    toggleTom2Selected(state) {
      state.voices.drumset.tom2Selected = !state.voices.drumset.tom2Selected;
    },
    toggleTom3Selected(state) {
      state.voices.drumset.tom3Selected = !state.voices.drumset.tom3Selected;
    },
    toggleTom4Selected(state) {
      state.voices.drumset.tom4Selected = !state.voices.drumset.tom4Selected;
    },
    toggleDotSelected(state) {
      state.dotSelected = !state.dotSelected;
    },
    toggleTupletSelected(state) {
      state.tuplet.selected = !state.tuplet.selected;
    },
    changeTupletActualDuration(state, action) {
      state.tuplet.actual = action.payload;
    },
    changeTupletNormalDuration(state, action) {
      state.tuplet.normal = action.payload;
    },
    changeTupletType(state, action) {
      state.tuplet.type = action.payload;
    },

    toggleSpockSelected(state) {
      state.voices.tenors.spockSelected = !state.voices.tenors.spockSelected;
    },
    toggleTenor1Selected(state) {
      state.voices.tenors.t1Selected = !state.voices.tenors.t1Selected;
    },
    toggleTenor2Selected(state) {
      state.voices.tenors.t2Selected = !state.voices.tenors.t2Selected;
    },
    toggleTenor3Selected(state) {
      state.voices.tenors.t3Selected = !state.voices.tenors.t3Selected;
    },
    toggleTenor4Selected(state) {
      state.voices.tenors.t4Selected = !state.voices.tenors.t4Selected;
    },

    toggleMarchingSnareSelected(state) {
      state.voices.snare.snareSelected = !state.voices.snare.snareSelected;
    },
    togglePingSelected(state) {
      state.voices.snare.pingSelected = !state.voices.snare.pingSelected;
    },
    toggleRimSelected(state) {
      state.voices.snare.rimSelected = !state.voices.snare.rimSelected;
    },
    toggleStickClickSelected(state) {
      state.voices.snare.stickClickSelected = !state.voices.snare.stickClickSelected;
    },
    toggleCrossoverSelected(state) {
      state.voices.snare.crossoverSelected = !state.voices.snare.crossoverSelected;
    },
    toggleBackstickSelected(state) {
      state.voices.snare.backstickSelected = !state.voices.snare.backstickSelected;
    },
    toggleButtSelected(state) {
      state.voices.snare.buttSelected = !state.voices.snare.buttSelected;
    },

    toggleB1Selected(state) {
      state.voices.bass.b1Selected = !state.voices.bass.b1Selected;
    },
    toggleB2Selected(state) {
      state.voices.bass.b2Selected = !state.voices.bass.b2Selected;
    },
    toggleB3Selected(state) {
      state.voices.bass.b3Selected = !state.voices.bass.b3Selected;
    },
    toggleB4Selected(state) {
      state.voices.bass.b4Selected = !state.voices.bass.b4Selected;
    },
    toggleB5Selected(state) {
      state.voices.bass.b5Selected = !state.voices.bass.b5Selected;
    },

    toggleCrashSelected(state) {
      state.voices.cymbals.crashSelected = !state.voices.cymbals.crashSelected;
    },
    toggleChokeSelected(state) {
      state.voices.cymbals.chokeSelected = !state.voices.cymbals.chokeSelected;
    },
  },
});

export default scoreSlice.reducer;
export const scoreActions = scoreSlice.actions;

export const getSelectedInstrument = createSelector(
  [(state) => state.selectedPartIndex, (state) => state.score],
  (selectedPartIndex, score) => {
    if ((selectedPartIndex != null) & (selectedPartIndex >= 0)) {
      return score.measures[0].parts[selectedPartIndex]?.instrument ?? null;
    } else {
      return null;
    }
  }
);

export const getScoreInstruments = createSelector(
  [(state) => state.score],
  (score) => score.measures[0].parts.map((part) => part.instrument)
);

export const getSelectedNote = createSelector(
  [(state) => state.selectedNoteIndex, (state) => state.score],
  (selectedNoteIndex, score) => {
    if (selectedNoteIndex) {
      const { measureIndex, partIndex, voiceIndex, noteIndex } =
        selectedNoteIndex;
      const part = score.measures[measureIndex].parts[partIndex];

      //We have to clone because the original object is not extensible.
      let selectedNote = _.cloneDeep(part.voices[voiceIndex].notes[noteIndex]);
      selectedNote.measureIndex = measureIndex;
      selectedNote.partIndex = partIndex;
      selectedNote.voiceIndex = voiceIndex;
      selectedNote.noteIndex = noteIndex;
      selectedNote.instrument = part.instrument;
      return selectedNote;
    } else {
      return null;
    }
  }
);

//Assumes one part for now.
export const getScoreVoices = createSelector(
  [(state) => state.score],
  (score) => {
    return getScoreVoicesUtil(score)
  }
);

// The notes for playback, in playing order: repeats, endings and D.C./D.S. jumps (see
// playbackOrder), measure repeats and beat slashes playing the measure they stand for, dynamics
// and hairpins setting loudness, and accel./rit. ramping the tempo. measureTimes[i] is when
// measure i first plays, for practice loops. A bar with takes plays the one its rule picks each
// time through (src/lib/takes.js), which changes from one playing (pass) to the next.
export const getToneJs = createSelector(
  [(state) => state.score, (state) => state.tempo, (state) => state.takePass || 0],
  (score, tempo, pass) => {
    const { measures, parts: partConfig } = score;
    const spb = 60 / tempo, order = playbackOrder(measures);
    const toneJsNotes = [], measureTimes = [];
    // Each voice's loudness carries on from measure to measure; null until its first dynamic.
    const loudness = new Map(), times = new Map();
    let start = 0, factor = 1;
    order.forEach((measureIndex, orderIndex) => {
      const written = measures[measureIndex], sounding = measures[soundingMeasure(measures, measureIndex)];
      // A section ends where the music jumps or repeats, or the next bar starts a repeat or a mark.
      const time = times.get(measureIndex) || 0, next = order[orderIndex + 1];
      times.set(measureIndex, time + 1);
      const sectionEnds = next !== measureIndex + 1 || Boolean(measures[next]?.repeatStart || measures[next]?.mark);
      const measure = sounding.takes?.length > 1 ? { ...sounding, parts: takeToPlay(sounding, { time, pass, sectionEnds, salt: measureIndex }) } : sounding;
      const quarters = written.timeSig.num * 4 / written.timeSig.type;
      // Seconds from the measure's start to a point in it (in quarter notes), with the tempo
      // ramping from `from` to `to` across the measure.
      // A tempo mark sets the speed from this bar on.
      if (written.tempo) factor = written.tempo / tempo;
      const from = factor, to = written.tempoChange ? factor * TEMPO_CHANGE_RATIO[written.tempoChange] : factor;
      const slope = (to - from) / quarters;
      const at = (position) => Math.abs(slope) < 1e-9 ? spb * position / from : spb / slope * Math.log((from + slope * position) / from);
      measure.parts.filter((part) => partConfig[part.instrument]?.enabled !== false).forEach((part) => {
        const { instrument } = part, pitched = isPitched(instrument), drawn = soundsOf(instrument) || pitched;
        part.voices.forEach((voice, voiceIndex) => {
          const key = `${instrument}:${voiceIndex}`;
          if (!loudness.has(key)) loudness.set(key, { level: null });
          const state = loudness.get(key);
          let position = 0;
          voice.notes.forEach((note, noteIndex) => {
            const length = noteQuarterDuration(note, noteIndex, voice.tuplets || []);
            const time = start + at(position), noteSecondsDuration = at(position + length) - at(position);
            position += length;
            // Loudness: the latest dynamic, shaped by a hairpin over this note.
            const dynamic = note.dynamic;
            let level = state.level;
            if (DYNAMIC_LEVELS[dynamic]) level = state.level = DYNAMIC_LEVELS[dynamic];
            if (dynamic === "fp") { level = DYNAMIC_LEVELS.f; state.level = DYNAMIC_LEVELS.p; }
            if (dynamic === "pf") { level = DYNAMIC_LEVELS.p; state.level = DYNAMIC_LEVELS.f; }
            const hairpin = (voice.hairpins || []).find((range) => noteIndex >= range.start && noteIndex < range.end);
            if (hairpin) {
              if (noteIndex === hairpin.start) state.base = level ?? DYNAMIC_LEVELS.mf;
              const target = Math.min(1, Math.max(.1, state.base + (hairpin.type === "cresc" ? .25 : -.25)));
              level = state.base + (target - state.base) * (noteIndex - hairpin.start + 1) / (hairpin.end - hairpin.start);
              if (noteIndex === hairpin.end - 1) state.level = target;
            }
            const accented = Boolean(note.ornaments?.includes(ACCENT) || note.ornaments?.includes(MARCATO));
            const shaped = (unmarked) => level == null ? (accented ? ACCENT_VELOCITY : unmarked) : accented ? Math.min(1, level + .3) : level;
            let velocity = shaped(pitched ? 0.7 : NON_ACCENT_VELOCITY);
            if (dynamic === "sfz" || dynamic === "rfz") velocity = Math.max(.9, (level ?? DYNAMIC_LEVELS.mf) + .4);
            velocity = Math.min(1, velocity);
            // A tied note carries on the one before it, rather than sounding again.
            const tied = state.tied;
            state.tied = null;
            if (/^pizz/i.test(note.text || "")) state.plucked = true;
            if (/^arco/i.test(note.text || "")) state.plucked = false;
            if (note.notes.length && tied) {
              for (const event of tied) if (event.duration != null) event.duration += noteSecondsDuration;
              if (note.tie) state.tied = tied;
              toneJsNotes.push({});
              return;
            }
            const started = toneJsNotes.length, ornaments = note.ornaments || "";

            if (note.notes.length && drawn && ornaments.includes(BUZZ)) {
              // A roll or tremolo, as quick strokes.
              const stroke = Math.max(ROLL_SECONDS, noteSecondsDuration / 32);
              for (let offset = 0; offset < noteSecondsDuration - 1e-6; offset += stroke) toneJsNotes.push({ time: time + offset, note: [...note.notes], duration: stroke, instrument, velocity: velocity * (offset ? 0.8 : 1) });
            } else if (note.notes.length && pitched) {
              // A chord, held for its written length, or shorter when staccato.
              const held = noteSecondsDuration * (ornaments.includes(STACCATO) ? 0.45 : 1);
              const chord = { time, note: [...note.notes], duration: held, instrument, velocity, ...(state.plucked && instrumentOf(instrument).family === "strings" ? { sound: "pizzicato" } : {}) };
              toneJsNotes.push(chord);
              if (ornaments.includes(DIDDLE)) { chord.duration = held / 2; toneJsNotes.push({ ...chord, time: time + held / 2 }); }
              if (ornaments.includes(FLAM)) toneJsNotes.push({ ...chord, time: time - 0.0175, duration: 0.04, velocity: Math.max(GRACE_VELOCITY, velocity * 0.4) });
            } else if (note.notes.length) {
              for (const pitch of note.notes) {
                const toneJsNote = { time, note: ornaments.includes(BUZZ) ? "G5" : pitch, instrument, velocity };
                toneJsNotes.push(toneJsNote);
                // The second stroke of a diddle (or cheese) and a flam's grace note.
                const diddleVelocity = level == null ? note.velocity : velocity;
                if (ornaments.includes(DIDDLE) || ornaments.includes(CHEESE)) toneJsNotes.push({ ...toneJsNote, time: time + noteSecondsDuration / 2, velocity: diddleVelocity || velocity });
                if (ornaments.includes(FLAM) || ornaments.includes(CHEESE)) toneJsNotes.push({ ...toneJsNote, time: time - 0.0175, velocity: GRACE_VELOCITY });
              }
            } else {
              //rest
              toneJsNotes.push({});
            }
            if (note.tie && note.notes.length) state.tied = toneJsNotes.slice(started);
          });
        });
      });
      const length = at(quarters);
      if (!measureTimes[measureIndex]) measureTimes[measureIndex] = { start, end: start + length };
      start += length;
      factor = to;
    });
    return { notes: toneJsNotes, duration: start, numMeasures: order.length, measureTimes, order };
  }
);

// Typing while a note on a pitched part is selected: A-G write (Shift adds to the chord), the up
// and down arrows move a semitone (an octave with Ctrl), Backspace makes a rest and H switches
// staffs. Returns whether the key was used, so drum shortcuts (A for accent…) apply otherwise.
export const pianoKey = (event) => (dispatch, getState) => {
  const state = getState().score.present, index = state.selectedNoteIndex;
  if (!isPitched(state.score.measures[index?.measureIndex]?.parts[index.partIndex]?.instrument)) return false;
  const command = event.ctrlKey || event.metaKey;
  if (/^[a-g]$/i.test(event.key) && !command) dispatch(scoreActions.pianoWrite({ letter: event.key.toUpperCase(), chord: event.shiftKey }));
  else if (event.key === "ArrowUp" || event.key === "ArrowDown") dispatch(scoreActions.pianoTranspose((event.key === "ArrowUp" ? 1 : -1) * (command ? 12 : 1)));
  else if ((event.key === "Backspace" || event.key === "Delete") && !command) dispatch(scoreActions.pianoRest());
  else if (/^h$/i.test(event.key) && !command) dispatch(scoreActions.pianoSwitchHand());
  else return false;
  return true;
};

export const selectNote = (note, scrollAmount) => {
  return async (dispatch) => {
    dispatch(
      scoreActions.selectNote({
        measureIndex: note.measureIndex,
        partIndex: note.partIndex,
        voiceIndex: note.voiceIndex,
        noteIndex: note.noteIndex,
      })
    );
    dispatch(scoreActions.setScrollAmount(scrollAmount));
  };
};

export const modifyNote = (noteData, scrollAmount) => {
  return async (dispatch) => {
    dispatch(scoreActions.modifyNote(noteData));
    dispatch(scoreActions.setScrollAmount(scrollAmount));
  };
};
